import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Customers from './pages/Customers';
import Catalog from './pages/Catalog';
import Inquiries from './pages/Inquiries';
import Projects from './pages/Projects';
import Partners from './pages/Partners';
import Orders from './pages/Orders';
import Visits from './pages/Visits';
import Users from './pages/Users';
import AuditLogs from './pages/AuditLogs';
import Analytics from './pages/Analytics';
import HR from './pages/HR';
import PurchaseOrders from './pages/PurchaseOrders';
import Quotes from './pages/Quotes';
import ProformaInvoiceOrders from './pages/ProformaInvoiceOrders';
import TaxInvoiceOrders from './pages/TaxInvoiceOrders';
import Profile from './pages/Profile';
import MyProfile from './pages/MyProfile';
import Accounting from './pages/Accounting';
import Receivables from './pages/Receivables';
import CompanySettings from './pages/CompanySettings';
import DataExport from './pages/DataExport';
import Attendance from './pages/Attendance';
import ServiceRequests from './pages/ServiceRequests';
import COA from './pages/COA';
import WebInquiries from './pages/WebInquiries';
import LicenseExpiry from './pages/LicenseExpiry';
import Activities from './pages/Activities';
import Tasks from './pages/Tasks';
import Territories from './pages/Territories';
import TVA from './pages/TVA';
import SampleManagement from './pages/SampleManagement';
import Competitors from './pages/Competitors';
import ChemStructure from './pages/ChemStructure';
import BalanceSheet from './pages/BalanceSheet';
import GSTReport from './pages/GSTReport';
import PublicLayout from './components/public/PublicLayout';
import { PWAInstallPrompt, PWAUpdateBanner, OfflineBanner } from './components/common/PWAPrompt';
import SessionWarning from './components/common/SessionWarning';
import Home from './pages/public/Home';
import PublicCatalog from './pages/public/PublicCatalog';
import ProductDetail from './pages/public/ProductDetail';
import About from './pages/public/About';
import Contact from './pages/public/Contact';
import WebhookSettings from './pages/WebhookSettings';
import CustomerPortal from './pages/CustomerPortal';
import EmailTemplates from './pages/EmailTemplates';
import CAPA from './pages/CAPA';
import RolePermissions from './pages/RolePermissions';
import ErrorBoundary from './components/common/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 10,   // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const PrivateRoute = ({ children, adminOnly, roles, department, pageKey }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div className="spinner spinner-lg" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  // Effective roles: multi-role array, falling back to single role
  const userRoles = user.roles?.length ? user.roles : (user.role ? [user.role] : []);

  // Admin, director and CEO are always allowed everything
  if (userRoles.some(r => ['admin', 'director', 'ceo'].includes(r))) return children;

  // Granular page permissions (user_page_permissions table).
  // If the user has ANY custom page grants, they are in whitelist mode:
  // ONLY explicitly listed pages are accessible — absence means denied.
  // Empty permissions object means no custom mode → fall through to role checks.
  if (pageKey && user.permissions && Object.keys(user.permissions).length > 0) {
    const perm = user.permissions[pageKey];
    if (!perm) return <Navigate to="/admin/dashboard" replace />;
    if (perm.expires_at && new Date(perm.expires_at) < new Date()) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return children;
  }

  // 1. Legacy: allowed_pages array (pre-granular-permissions users).
  if (pageKey && user.allowed_pages && Array.isArray(user.allowed_pages)) {
    return user.allowed_pages.includes(pageKey)
      ? children
      : <Navigate to="/admin/dashboard" replace />;
  }

  // 2. Department-gated pages: allow if user's department matches
  if (department) {
    const userDept = (user.department || '').toLowerCase().replace(/[\s_-]/g, '');
    const reqDept  = department.toLowerCase().replace(/[\s_-]/g, '');
    if (userDept === reqDept || userDept.includes(reqDept) || reqDept.includes(userDept)) return children;
    // Also allow if roles list includes any of the user's roles (manager fallback)
    if (roles && roles.some(r => userRoles.includes(r))) return children;
    return <Navigate to="/admin/dashboard" replace />;
  }

  // 3. Fallback to default role-based and admin-only checks
  if (adminOnly && !userRoles.includes('admin')) return <Navigate to="/admin/dashboard" replace />;
  if (roles && !roles.some(r => userRoles.includes(r))) return <Navigate to="/admin/dashboard" replace />;

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/admin/dashboard" replace />;
  return children;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
  <HelmetProvider>
  <AuthProvider>
    <CompanyProvider>
    <BrowserRouter>
      <OfflineBanner />
      <PWAUpdateBanner />
      <PWAInstallPrompt />
      <SessionWarning />
      <Routes>
        {/* Public pages */}
        <Route element={<ErrorBoundary><PublicLayout /></ErrorBoundary>}>
          <Route path="/" element={<Home />} />
          <Route path="/catalog" element={<PublicCatalog />} />
          <Route path="/catalog/:id" element={<ProductDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Route>

        {/* Customer Portal — public, token-secured */}
        <Route path="/portal/:token" element={<ErrorBoundary><CustomerPortal /></ErrorBoundary>} />

              {/* Auth */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

        {/* Admin */}
        <Route path="/admin" element={<PrivateRoute><ErrorBoundary><Layout /></ErrorBoundary></PrivateRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clients"           element={<PrivateRoute roles={['admin','manager','bd','sales','procurement']}                         pageKey="clients"><Clients /></PrivateRoute>} />
          <Route path="partners"          element={<PrivateRoute roles={['admin','manager','bd','sales','procurement']}                         pageKey="partners"><Partners /></PrivateRoute>} />
          <Route path="customers"         element={<PrivateRoute roles={['admin','manager','bd','mr','sales','finance']}                        pageKey="customers"><Customers /></PrivateRoute>} />
          <Route path="catalog"           element={<PrivateRoute pageKey="catalog"><Catalog /></PrivateRoute>} />
          <Route path="inquiries"         element={<PrivateRoute roles={['admin','manager','bd','mr','sales','rnd_head','scientist','principal_scientist']} pageKey="inquiries"><Inquiries /></PrivateRoute>} />
          <Route path="projects"          element={<PrivateRoute roles={['admin','manager','bd','scientist','rnd_head','principal_scientist']}   pageKey="projects"><Projects /></PrivateRoute>} />
          <Route path="orders"            element={<PrivateRoute roles={['admin','manager','bd','mr','sales','finance','procurement']}           pageKey="orders"><Orders /></PrivateRoute>} />
          <Route path="visits"            element={<PrivateRoute roles={['admin','manager','bd','mr','sales']}                                  pageKey="visits"><Visits /></PrivateRoute>} />
          <Route path="invoices"                  element={<Navigate to="/admin/tax-invoice-orders" replace />} />
          <Route path="proforma-invoices"         element={<Navigate to="/admin/proforma-invoice-orders" replace />} />
          <Route path="proforma-invoice-orders"   element={<PrivateRoute roles={['admin','manager','bd','finance','sales']}                     pageKey="proforma-invoice-orders"><ProformaInvoiceOrders /></PrivateRoute>} />
          <Route path="tax-invoice-orders"        element={<PrivateRoute roles={['admin','manager','bd','finance','sales']}                     pageKey="tax-invoice-orders"><TaxInvoiceOrders /></PrivateRoute>} />
          <Route path="purchase-orders"           element={<PrivateRoute roles={['admin','manager','bd','finance','procurement']}               pageKey="purchase-orders"><PurchaseOrders /></PrivateRoute>} />
          <Route path="quotes"            element={<PrivateRoute roles={['admin','manager','bd','mr','sales']}                                  pageKey="quotes"><Quotes /></PrivateRoute>} />
          <Route path="users"             element={<PrivateRoute roles={['admin','manager','software']}                                         pageKey="users"><Users /></PrivateRoute>} />
          <Route path="audit-logs"        element={<PrivateRoute roles={['admin','manager','software']}                                         pageKey="audit-logs"><AuditLogs /></PrivateRoute>} />
          <Route path="company-settings"  element={<PrivateRoute adminOnly pageKey="company-settings"><CompanySettings /></PrivateRoute>} />
          <Route path="data-export"       element={<PrivateRoute roles={['admin','manager','rnd_head','finance','software']}                    pageKey="data-export"><DataExport /></PrivateRoute>} />
          <Route path="analytics"         element={<PrivateRoute roles={['admin','manager','rnd_head','finance']}                               pageKey="analytics"><Analytics /></PrivateRoute>} />
          <Route path="hr"                element={<PrivateRoute department="hr" roles={['admin','manager']} pageKey="hr"><HR /></PrivateRoute>} />
          <Route path="attendance"         element={<PrivateRoute pageKey="attendance"><Attendance /></PrivateRoute>} />
          <Route path="service-requests"  element={<PrivateRoute pageKey="service-requests"><ServiceRequests /></PrivateRoute>} />
          <Route path="accounting"        element={<PrivateRoute roles={['admin','manager','finance','procurement']}                            pageKey="accounting"><Accounting /></PrivateRoute>} />
          <Route path="receivables"       element={<PrivateRoute roles={['admin','manager','finance']}                                          pageKey="receivables"><Receivables /></PrivateRoute>} />
          <Route path="coa"               element={<PrivateRoute roles={['admin','manager','bd','scientist','rnd_head','principal_scientist']}   pageKey="coa"><COA /></PrivateRoute>} />
          <Route path="web-inquiries"     element={<PrivateRoute roles={['admin','manager']}                                                    pageKey="web-inquiries"><WebInquiries /></PrivateRoute>} />
          <Route path="license-expiry"    element={<PrivateRoute roles={['admin','manager','mr','bd','sales']}                                  pageKey="license-expiry"><LicenseExpiry /></PrivateRoute>} />
          <Route path="activities"        element={<PrivateRoute roles={['admin','manager','mr','bd','sales']}                                  pageKey="activities"><Activities /></PrivateRoute>} />
          <Route path="tasks"             element={<PrivateRoute pageKey="tasks"><Tasks /></PrivateRoute>} />
          <Route path="territories"       element={<PrivateRoute roles={['admin','manager']}                                                    pageKey="territories"><Territories /></PrivateRoute>} />
          <Route path="tva"               element={<PrivateRoute roles={['admin','manager','mr','bd','sales']}                                  pageKey="tva"><TVA /></PrivateRoute>} />
          <Route path="samples"           element={<PrivateRoute roles={['admin','manager','mr','bd','sales']}                                  pageKey="samples"><SampleManagement /></PrivateRoute>} />
          <Route path="competitors"       element={<PrivateRoute roles={['admin','manager','mr','bd','sales']}                                  pageKey="competitors"><Competitors /></PrivateRoute>} />
          <Route path="chem-structure"    element={<PrivateRoute roles={['admin','manager','bd','scientist','rnd_head','principal_scientist']}   pageKey="chem-structure"><ChemStructure /></PrivateRoute>} />
          <Route path="balance-sheet"     element={<PrivateRoute roles={['admin','manager','finance']}                                          pageKey="balance-sheet"><BalanceSheet /></PrivateRoute>} />
          <Route path="gst-report"        element={<PrivateRoute roles={['admin','manager','finance']}                                          pageKey="gst-report"><GSTReport /></PrivateRoute>} />
          <Route path="webhooks"          element={<PrivateRoute roles={['admin','software']} pageKey="webhooks"><WebhookSettings /></PrivateRoute>} />
          <Route path="email-templates"   element={<PrivateRoute roles={['admin','manager']} pageKey="email-templates"><EmailTemplates /></PrivateRoute>} />
          <Route path="capa"              element={<PrivateRoute roles={['admin','manager','rnd_head','principal_scientist','scientist']} pageKey="capa"><CAPA /></PrivateRoute>} />
          <Route path="role-permissions"  element={<PrivateRoute adminOnly pageKey="role-permissions"><RolePermissions /></PrivateRoute>} />
          <Route path="profile" element={<Profile />} />
          <Route path="my-profile" element={<MyProfile />} />
        </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CompanyProvider>
      </AuthProvider>
    </HelmetProvider>
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
  </QueryClientProvider>
);

export default App;
